import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 20, bottom: 20, left: 20 };
const MIN_NODE_HEIGHT = 20;
const NODE_PADDING = 8;
const NODE_WIDTH = 18;
const NODE_RADIUS = 4;

const TIER_COLORS = ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#1e3a8a'];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

/**
 * Collects all unique date labels (columns) from the periods array.
 * Each period has fromLabel and toLabel; consecutive periods share an edge.
 */
function getColumnLabels(periods) {
  const labels = [];
  periods.forEach((p, i) => {
    if (i === 0) labels.push(p.fromLabel);
    labels.push(p.toLabel);
  });
  return labels;
}

/**
 * Computes counts per tier for each column by summing flows.
 * For column i (not the last), the "from" side of period i gives tier counts.
 * For the last column, the "to" side of the last period gives tier counts.
 */
function computeColumnTierCounts(periods, tiers, flows) {
  const numColumns = periods.length + 1;
  const counts = Array.from({ length: numColumns }, () => {
    const map = {};
    tiers.forEach((t) => (map[t] = 0));
    return map;
  });

  flows.forEach((flow) => {
    const colFrom = flow.periodIndex;
    const colTo = flow.periodIndex + 1;
    counts[colFrom][flow.fromTier] += flow.count;
    counts[colTo][flow.toTier] += flow.count;
  });

  return counts;
}

/**
 * Lays out nodes vertically within a column.
 * Returns an array of { tier, y, height, count, color } for each tier that has count > 0.
 */
function layoutColumn(tierCounts, tiers, availableHeight) {
  const activeTiers = tiers.filter((t) => tierCounts[t] > 0);
  if (activeTiers.length === 0) return [];

  const totalCount = activeTiers.reduce((sum, t) => sum + tierCounts[t], 0);
  const totalPadding = (activeTiers.length - 1) * NODE_PADDING;
  const drawableHeight = Math.max(availableHeight - totalPadding, activeTiers.length * MIN_NODE_HEIGHT);

  // First pass: proportional heights with min enforcement
  let nodes = activeTiers.map((tier, i) => {
    const tierIndex = tiers.indexOf(tier);
    const color = TIER_COLORS[Math.min(tierIndex, TIER_COLORS.length - 1)];
    const rawHeight = totalCount > 0 ? (tierCounts[tier] / totalCount) * drawableHeight : MIN_NODE_HEIGHT;
    return {
      tier,
      count: tierCounts[tier],
      height: Math.max(rawHeight, MIN_NODE_HEIGHT),
      color,
    };
  });

  // Normalize heights to fit if they exceed available space
  const totalNodeHeight = nodes.reduce((s, n) => s + n.height, 0);
  const totalNeeded = totalNodeHeight + totalPadding;
  if (totalNeeded > availableHeight && availableHeight > 0) {
    const scale = (availableHeight - totalPadding) / totalNodeHeight;
    nodes.forEach((n) => {
      n.height = Math.max(n.height * scale, MIN_NODE_HEIGHT);
    });
  }

  // Assign y positions
  let y = 0;
  nodes.forEach((n) => {
    n.y = y;
    y += n.height + NODE_PADDING;
  });

  return nodes;
}

export default function SankeyDiagram({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.periods || data.periods.length === 0 || !data.flows || data.flows.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { periods, tiers, flows } = data;
    const columnLabels = getColumnLabels(periods);
    const numColumns = columnLabels.length;
    const columnCounts = computeColumnTierCounts(periods, tiers, flows);

    // Sizing
    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);

    // Determine height based on max addresses in any column
    const maxCount = Math.max(...columnCounts.map((cc) => Object.values(cc).reduce((s, v) => s + v, 0)));
    const activeTierCount = Math.max(...columnCounts.map((cc) => tiers.filter((t) => cc[t] > 0).length));
    const estimatedHeight = Math.max(
      300,
      activeTierCount * MIN_NODE_HEIGHT + (activeTierCount - 1) * NODE_PADDING + MARGIN.top + MARGIN.bottom + 40,
      maxCount * 4 + MARGIN.top + MARGIN.bottom
    );
    const height = Math.min(estimatedHeight, 600);

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Column x positions
    const columnX = [];
    for (let i = 0; i < numColumns; i++) {
      columnX.push((i / (numColumns - 1)) * (innerW - NODE_WIDTH));
    }

    // Layout all columns
    const columnNodes = columnCounts.map((cc, i) => layoutColumn(cc, tiers, innerH));

    // Draw column date labels
    columnLabels.forEach((label, i) => {
      g.append('text')
        .attr('x', columnX[i] + NODE_WIDTH / 2)
        .attr('y', -14)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', '#6b7280')
        .text(label);
    });

    // Draw nodes
    columnNodes.forEach((nodes, colIdx) => {
      nodes.forEach((node) => {
        // Node rect
        g.append('rect')
          .attr('x', columnX[colIdx])
          .attr('y', node.y)
          .attr('width', NODE_WIDTH)
          .attr('height', node.height)
          .attr('rx', NODE_RADIUS)
          .attr('ry', NODE_RADIUS)
          .attr('fill', node.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 0.5);

        // Node label (to the right for leftmost column, to the left for rightmost, right otherwise)
        const labelText = `${node.tier} (${node.count})`;
        const isLast = colIdx === numColumns - 1;
        const labelX = isLast ? columnX[colIdx] - 4 : columnX[colIdx] + NODE_WIDTH + 4;
        const labelAnchor = isLast ? 'end' : 'start';

        if (node.height >= 14) {
          g.append('text')
            .attr('x', labelX)
            .attr('y', node.y + node.height / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', labelAnchor)
            .attr('font-size', '10px')
            .attr('fill', '#374151')
            .attr('font-weight', '500')
            .text(labelText);
        }
      });
    });

    // Draw links/flows between columns
    flows.forEach((flow) => {
      const srcColIdx = flow.periodIndex;
      const tgtColIdx = flow.periodIndex + 1;

      const srcNodes = columnNodes[srcColIdx];
      const tgtNodes = columnNodes[tgtColIdx];

      const srcNode = srcNodes.find((n) => n.tier === flow.fromTier);
      const tgtNode = tgtNodes.find((n) => n.tier === flow.toTier);

      if (!srcNode || !tgtNode || flow.count === 0) return;

      // Calculate link thickness proportional to flow count relative to source node count
      const srcTotal = srcNode.count;
      const linkHeight = srcTotal > 0 ? (flow.count / srcTotal) * srcNode.height : 0;
      if (linkHeight < 0.5) return;

      // Calculate vertical offsets within the source and target nodes.
      // We need to track how much of each node has been "used" by previous flows.
      // Since we process all flows in order, we track offsets on the node objects.
      if (srcNode._srcOffset == null) srcNode._srcOffset = 0;
      if (tgtNode._tgtOffset == null) tgtNode._tgtOffset = 0;

      const tgtTotal = tgtNode.count;
      const tgtLinkHeight = tgtTotal > 0 ? (flow.count / tgtTotal) * tgtNode.height : 0;

      const srcY0 = srcNode.y + srcNode._srcOffset;
      const srcY1 = srcY0 + linkHeight;
      const tgtY0 = tgtNode.y + tgtNode._tgtOffset;
      const tgtY1 = tgtY0 + tgtLinkHeight;

      srcNode._srcOffset += linkHeight;
      tgtNode._tgtOffset += tgtLinkHeight;

      const x0 = columnX[srcColIdx] + NODE_WIDTH;
      const x1 = columnX[tgtColIdx];

      // Build a cubic bezier path (area between two horizontal links)
      const midX = (x0 + x1) / 2;
      const pathData = `
        M ${x0},${srcY0}
        C ${midX},${srcY0} ${midX},${tgtY0} ${x1},${tgtY0}
        L ${x1},${tgtY1}
        C ${midX},${tgtY1} ${midX},${srcY1} ${x0},${srcY1}
        Z
      `;

      const isSameTier = flow.fromTier === flow.toTier;
      const baseOpacity = isSameTier ? 0.15 : 0.3;
      const hoverOpacity = 0.6;

      g.append('path')
        .attr('d', pathData)
        .attr('fill', srcNode.color)
        .attr('opacity', baseOpacity)
        .attr('stroke', 'none')
        .style('cursor', 'pointer')
        .on('mouseenter', function (event) {
          d3.select(this).attr('opacity', hoverOpacity);
          const message = isSameTier
            ? `${flow.count} address${flow.count !== 1 ? 'es' : ''} stayed in ${flow.fromTier}`
            : `${flow.count} address${flow.count !== 1 ? 'es' : ''} moved from ${flow.fromTier} to ${flow.toTier}`;
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            message,
          });
        })
        .on('mousemove', function (event) {
          setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : prev);
        })
        .on('mouseleave', function () {
          d3.select(this).attr('opacity', baseOpacity);
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // No data or insufficient periods
  if (
    !data ||
    !data.periods ||
    data.periods.length === 0 ||
    !data.flows ||
    data.flows.length === 0
  ) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Tier Migration</div>
        <div style={styles.subtitle}>How addresses move between price bands over time</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          Not enough data for migration analysis — need at least 2 scrape dates
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Tier Migration</div>
      <div style={styles.subtitle}>How addresses move between price bands over time</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937' }}>{tooltip.message}</div>
        </div>
      )}
    </div>
  );
}
